<template>
  <v-card
    :color="cardColor"
    :elevation="cardElevation"
    :class="{
      'white--text': isDarkTheme,
      'hyp-article-is-read': isRead,
    }"
    :style="cardStyle"
  >
    <v-row dense row wrap>
      <v-col
        class="body-2 pt-2 pb-0 col-12 cursor-pointer"
        :class="{ 'hyp-title-container': !verifiedEvent }"
        @click="toggleSummary()"
      >
        <div class="flexVerticalAlign">
          <trend-indicator
            class="trendIndicator"
            v-if="trend"
            :trend="trend"
          ></trend-indicator>
          <div
            class="pull-left hyp-title application-heading breakText"
            :style="titleColor"
          >
            {{ getArticleTitle(esId) }}
            <v-img
              v-if="isTwitterVerified"
              width="1.5em"
              height="1.5em"
              class="filter-blue verifiedImg"
              src="@/assets/svgs/verified.svg"
            >
            </v-img>
          </div>
        </div>
      </v-col>
      <v-col v-if="alertName">
        <div class="pull-left mr-2 hyp-source-alert-title">
          Alert: {{ alertName }}
        </div>
      </v-col>
      <v-col v-if="verifiedEvent && assetProximityText">
        <div class="pull-left mr-2 hyp-source-alert-title">
          {{ assetProximityText }}
        </div>
      </v-col>
    </v-row>

    <v-row dense row wrap v-if="showSummary && !verifiedEvent && !discoverAi">
      <v-col class="caption hyp-details-list-item-summary col-11 breakText">
        <v-img
          v-if="showImage && img && !imgFailed"
          height="80"
          width="80"
          class="pull-left mr-2 mt-1"
          alt="article image"
          :src="img"
          @error="onImgError"
        ></v-img>
        <DetailsListItemClickable
          :show-more-link="showMoreLess"
          :content="getArticleSummary(esId)"
        />
        <div class="clearfix"></div>

        <div v-if="isAnalysis && !isTravelISOS" class="mt-2 text-center">
          <v-btn text x-small @click="handleClickShowAnalysisDetails"
            >View complete analysis</v-btn
          >
        </div>
        <div v-if="isTravelISOS" class="mt-2 text-center">
          <v-btn text x-small @click="handleClickShowAnalysisDetails"
            >View travel alert</v-btn
          >
        </div>
        <div v-if="isBreakingEvent" class="mt-2 text-center">
          <v-btn text x-small @click="handleClickShowBreakingEventDetails"
            >View complete breaking event</v-btn
          >
        </div>
      </v-col>
    </v-row>

    <v-row
      dense
      row
      wrap
      v-if="showInlineBody && showBody && !verifiedEvent && !discoverAi"
    >
      <v-col class="caption col-11 breakText">
        <DetailsListItemClickable
          :class="{ 'item-body-dark-mode': theme === 'dark' }"
          :content="getArticleBody(esId)"
        />
      </v-col>
    </v-row>

    <!--Verified Events-->
    <div v-if="verifiedEvent">
      <v-row
        v-if="countriesDisplayMetadata"
        class="mt-0 mb-0 pb-0 pt-0"
        align="baseline"
      >
        <v-col
          xs12
          class="my-1 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <DetailsListItemLocations
            :countries="countriesDisplayMetadata"
            :theme="theme"
          ></DetailsListItemLocations>
        </v-col>
      </v-row>
      <v-row v-if="waters" class="mt-0 mb-0 pb-0 pt-0" align="baseline">
        <v-col
          xs12
          class="my-1 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <DetailsListItemLocations
            :waters="waters"
            :theme="theme"
          ></DetailsListItemLocations>
        </v-col>
      </v-row>
      <v-row v-if="categories" class="mt-0 mb-0 pb-0 pt-0" align="baseline">
        <v-col
          xs12
          class="mt-0 mb-0 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <span class="application-heading">Categories</span>: {{ categories }}
        </v-col>
      </v-row>
      <v-row
        v-if="sectors && showSummary"
        class="mt-0 mb-0 pb-0 pt-0"
        align="baseline"
      >
        <v-col
          xs12
          class="mt-0 mb-0 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <span class="application-heading">Sectors</span>: {{ sectors }}
        </v-col>
      </v-row>
      <v-row
        v-if="assets && showSummary"
        class="mt-0 mb-0 pb-0 pt-0"
        align="baseline"
      >
        <v-col
          xs12
          class="mt-0 mb-0 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <span class="application-heading">Assets</span>: {{ assets }}
        </v-col>
      </v-row>
      <v-row
        v-if="damageRating && showSummary"
        class="mt-0 mb-0 pb-0 pt-0"
        align="baseline"
      >
        <v-col
          xs12
          class="mt-0 mb-0 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <span class="application-heading">Damage Rating</span>:
          {{ damageRating }}
        </v-col>
      </v-row>
      <v-row
        v-if="showSummary && getLatLon && getLatLon.lat && getLatLon.lon"
        class="mt-0 mb-0 pb-0 pt-0"
        align="baseline"
      >
        <v-col
          xs12
          class="mt-0 mb-0 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <span class="application-heading">Lat</span>: {{ getLatLon.lat }}
          <span class="application-heading">Lon</span>: {{ getLatLon.lon }}
        </v-col>
      </v-row>
      <v-row
        v-if="showSummary && severity"
        class="mt-0 mb-0 pb-0 pt-0"
        align="baseline"
      >
        <v-col
          xs12
          class="mt-0 mb-0 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <span class="application-heading">Severity</span>: {{ severity }}
        </v-col>
      </v-row>
      <v-row v-if="references" class="mt-0 mb-0 pb-0 pt-0" align="baseline">
        <v-col
          xs12
          class="mt-0 mb-0 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <span class="application-heading">Sources</span>:
          <span v-html="references" class="hyp-sources-container"></span>
        </v-col>
      </v-row>
    </div>

    <!-- The logic of DetailsListItemExtras requires if the input parameter :countries is true then
         :analysis-info also needs to be true -->
    <v-row
      v-if="
        !verifiedEvent &&
        (typeof sentiment === 'number' ||
          topicsDisplayList ||
          influenceDisplayList ||
          analysisInfo ||
          originalLanguage ||
          (countriesDisplayMetadata && (analysisInfo || countriesWithFlags)))
      "
      dense
      row
      wrap
    >
      <v-col class="col-12 ma-1 mt-2 pl-0" v-if="!discoverAi">
        <DetailsListItemExtras
          class="ma-0 mt-0"
          :sentiment="sentiment"
          :emotions="emotions"
          :reliability="reliability"
          :social-authenticity="socialAuthenticity"
          :high-risk-tag="highRiskTag"
          :source-type="sourceType"
          :social-handle="author"
          :social-follower-count="socialFollowerCount"
          :topics-display-list="topicsDisplayList"
          :influence-display-list="influenceDisplayList"
          :analysis-info="analysisInfo || countriesWithFlags"
          :severity="severity"
          :analytics-category="analyticsCategory"
          :original-language="originalLanguage"
          :countries="countriesDisplayMetadata"
          :waters="waters"
          :theme="theme"
          :expanded="showSummary"
          ><template v-slot:sourceLink>
            <a @click.stop.prevent="openSource(link)" :href="link">{{
              source
            }}</a>
          </template></DetailsListItemExtras
        >
      </v-col>
    </v-row>

    <!--Breaking Events-->
    <div v-if="isBreakingEvent">
      <v-row
        v-if="getLatLon && getLatLon.lat && getLatLon.lon"
        class="mt-0 mb-0 pb-0 pt-0"
        align="baseline"
      >
        <v-col
          xs12
          class="mt-0 mb-0 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <span class="application-heading">Lat</span>: {{ getLatLon.lat }}
          <span class="application-heading">Lon</span>: {{ getLatLon.lon }}
        </v-col>
      </v-row>
      <v-row v-if="link && source" class="mt-0 mb-0 pb-0 pt-0" align="baseline">
        <v-col
          xs12
          class="mt-0 mb-0 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <span class="application-heading">Source</span>:
          <a @click.stop.prevent="openSource(link)" :href="link" class="mr-2">{{
            source
          }}</a>
        </v-col>
      </v-row>
    </div>

    <!--Discover AI-->
    <div v-if="discoverAi">
      <v-row
        v-if="headlineCountry"
        class="mt-0 mb-1 pb-0 pt-0"
        align="baseline"
      >
        <v-col
          xs12
          class="my-1 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <DetailsListItemLocations
            :countries="headlineCountry"
            :theme="theme"
          ></DetailsListItemLocations>
        </v-col>
      </v-row>
      <v-row
        v-if="relatedCountries"
        class="mt-0 mb-1 pb-0 pt-0"
        align="baseline"
      >
        <v-col
          xs12
          class="my-1 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <DetailsListItemLocations
            :countries="relatedCountries"
            :theme="theme"
            title="Related Countries"
          ></DetailsListItemLocations>
        </v-col>
      </v-row>
      <v-row class="mt-0 mb-2 pb-0 pt-0 d-flex align-center justify-start">
        <v-col
          xs-auto
          class="mt-0 mb-0 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <v-tooltip bottom v-if="headlineStatus">
            <template v-slot:activator="{ on }">
              <v-chip :style="statusStyle" v-on="on" label small class="mr-2">{{
                headlineStatusDisplayName
              }}</v-chip>
            </template>
            <span>{{ headlineStatusText }}</span>
          </v-tooltip>
          <v-chip class="ai-articles" label small>
            {{ eventsAiArticles }}
          </v-chip>
        </v-col>
      </v-row>
    </div>

    <!--Breaking Events and Verified Events Dates-->
    <div v-if="verifiedEvent || isBreakingEvent">
      <v-row
        class="mt-0 mb-0 pb-0 pt-0"
        align="baseline"
        v-if="initialPublishedDate && revisedPublishedDate"
      >
        <v-col
          xs12
          class="mt-0 mb-0 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <span class="application-heading">Published Date</span>:
          {{ initialPublishedDate }}
        </v-col>
      </v-row>
      <v-row
        v-if="(verifiedEvent || isBreakingEvent) && displayRevisedDate.date"
      >
        <v-col
          xs12
          class="mt-0 mb-0 pb-0 pt-0 text-left hyp-details-list-item-topics"
        >
          <span class="application-heading">{{ displayRevisedDate.label }}</span
          >:
          {{ displayRevisedDate.date }}
        </v-col>
      </v-row>
    </div>

    <v-row dense row wrap>
      <v-col class="pt-0 py-1 hyp-date-source-container col-12">
        <div
          v-if="!verifiedEvent && !isBreakingEvent && dateTime"
          class="pull-left mr-2"
        >
          {{ dateTime }}
        </div>
        <div
          v-if="(verifiedEvent || isBreakingEvent) && eventTime"
          class="pull-left mr-2"
        >
          <span class="application-heading">Event Date</span>: {{ eventTime }}
        </div>

        <a
          v-if="
            link &&
            source &&
            !isBreakingEvent &&
            !isFollowerCountBracketUnlocked
          "
          @click.stop.prevent="openSource(link)"
          :href="link"
          class="pull-left mr-2"
          >{{ source }}</a
        >

        <DetailsActionIcons
          v-if="!discoverAi"
          :item="item"
          :show-thumbs-down="showThumbsDown"
          :show-thumbs-down-geo="showThumbsDownGeo"
          :show-primary-topics="false"
          :primary-topic="primaryTopic"
          :showing-documents="showingDocuments"
          :show-collaboration="showCollaboration"
          :cluster-id="clusterId"
          :theme="theme"
          :admin-info="adminInfo"
          :show-read-icon="actionIconsEnabled"
          :show-translate-icon="showTranslateIcon"
          :translating="getArticleTranslating(esId)"
          :translate-hint="getArticleTranslateHint(esId)"
          :read-hint="readHint"
          :has-document-note="hasDocumentNote"
          :show-copy-icon="showCopyIcon"
          :show-download-icon="isAnalysis"
          @click-collab-delete="handleDeleteClick"
          @click-read="handleReadClick"
          @click-translate="translate"
          @click-label-color="handleLabelColorClick"
          @click-document-note="handleDocumentNoteClick"
          @click-copy-content="handleCopyClick(item.properties.formatted)"
          class="pb-1 pull-left"
        ></DetailsActionIcons>

        <div class="clearfix"></div>
      </v-col>

      <v-col
        v-if="clusterSize > 1"
        class="py-1 hyp-date-source-container col-12"
      >
        <div class="pull-left hyp-toggle-container" @click="toggleArticles">
          <span class="ml-1 hyp-more-info hyp-more-info-toggle">
            <v-icon size="14" v-if="!showArticles" class="hyp-more-info"
              >fas fa-angle-down</v-icon
            >
            <v-icon size="14" v-if="showArticles" class="hyp-more-info"
              >fas fa-angle-up</v-icon
            >
            More Coverage
          </span>
        </div>
      </v-col>
    </v-row>

    <v-row row wrap dense v-if="showArticles">
      <v-col class="pt-1 pb-3 col-12">
        <div class="mx-4">
          <Loader
            v-if="articlesLoading"
            size="small"
            color="white"
            text=""
          ></Loader>
          <v-container
            v-if="articles && !articlesLoading"
            fluid
            grid-list-xs
            class="pa-0"
          >
            <v-row
              v-for="(article, index) in articles"
              :key="index"
              row
              wrap
              dense
            >
              <v-col x12>
                <v-container fluid grid-list-xs class="pa-0">
                  <v-row row wrap dense>
                    <v-col class="body-2 pt-2 pb-0 hyp-title-container col-11">
                      <span
                        class="pull-left hyp-title application-heading"
                        :style="titleColor"
                      >
                        {{ getArticleTitle(article.properties.formatted.esId) }}
                      </span>
                    </v-col>
                    <v-col v-if="article.properties.formatted.name">
                      <div class="pull-left mr-2 hyp-source-alert-title">
                        Alert: {{ article.properties.formatted.name }}
                      </div>
                    </v-col>
                  </v-row>

                  <v-row
                    v-if="
                      typeof sentiment === 'number' ||
                      topicsDisplayList ||
                      influenceDisplayList
                    "
                    dense
                    row
                    wrap
                  >
                    <DetailsListItemExtras
                      class="ma-1 mt-2"
                      :sentiment="sentiment"
                      :emotions="emotions"
                      :reliability="reliability"
                      :social-authenticity="socialAuthenticity"
                      :high-risk-tag="highRiskTag"
                      :source-type="sourceType"
                      :social-handle="author"
                      :social-follower-count="socialFollowerCount"
                      :topics-display-list="topicsDisplayList"
                      :influence-display-list="influenceDisplayList"
                      :theme="theme"
                      :expanded="showSummary"
                      ><template v-slot:sourceLink>
                        <a
                          @click.stop.prevent="
                            openSource(article.properties.formatted.link)
                          "
                          :href="article.properties.formatted.link"
                          class="pull-left"
                          v-if="
                            article.properties.formatted.link &&
                            article.properties.formatted.source
                          "
                          >{{ article.properties.formatted.source }}
                        </a>
                      </template></DetailsListItemExtras
                    >
                  </v-row>

                  <v-row row wrap dense>
                    <v-col class="py-1 hyp-date-source-container col-12">
                      <div class="pull-left mr-2">
                        {{
                          getArticleDate(article.properties.formatted.occurred)
                        }}
                      </div>
                      <a
                        @click.stop.prevent="
                          openSource(article.properties.formatted.link)
                        "
                        :href="article.properties.formatted.link"
                        class="pull-left mr-2"
                        v-if="
                          article.properties.formatted.link &&
                          article.properties.formatted.source &&
                          !isFollowerCountBracketUnlocked
                        "
                        >{{ article.properties.formatted.source }}
                      </a>

                      <DetailsActionIcons
                        :item="article"
                        :show-thumbs-down="showThumbsDown"
                        :show-thumbs-down-geo="showThumbsDownGeo"
                        :show-primary-topics="false"
                        :primary-topic="primaryTopic"
                        :showing-documents="showingDocuments"
                        :theme="theme"
                        :admin-info="adminInfo"
                        :show-read-icon="actionIconsEnabled"
                        :show-translate-icon="showTranslateIcon"
                        :translating="
                          getArticleTranslating(
                            article.properties.formatted.esId
                          )
                        "
                        :translate-hint="
                          getArticleTranslateHint(
                            article.properties.formatted.esId
                          )
                        "
                        :read-hint="readHint"
                        @click-collab-delete="handleDeleteClick"
                        @click-read="handleReadClick"
                        @click-translate="translate"
                        @click-copy-content="
                          handleCopyClick(article.properties.formatted)
                        "
                        class="pb-1 pull-left"
                      ></DetailsActionIcons>

                      <div class="clearfix"></div>
                    </v-col>
                  </v-row>
                </v-container>
              </v-col>
            </v-row>
          </v-container>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="showingDocuments && showNote" row dense>
      <v-col class="col-12">
        <v-textarea
          v-model="documentNote"
          placeholder="Enter a document note"
          :rows="2"
          :loading="noteSaving"
          class="hyp-doc-note-textarea pt-0 mt-0 mx-3"
          @change="handleUpdateDocumentNote"
        ></v-textarea>
      </v-col>
    </v-row>

    <LeaveAppPopup
      :link="sourceLink"
      :show="showSourceLink"
      :high-risk="highRiskTag"
    ></LeaveAppPopup>
  </v-card>
</template>

<script>
import lodashGet from "lodash/get";
import lodashHas from "lodash/has";
import lodashIsEqual from "lodash/isEqual";
import lodashDebounce from "lodash/debounce";
import lodashFind from "lodash/find";
import lodashCloneDeep from "lodash/cloneDeep";

import { mapGetters } from "vuex";

import {
  MODULE_PREFIX_USER,
  MODULE_PREFIX_HOME,
  MODULE_PREFIX_ANALYSIS,
  MODULE_PREFIX_SEARCH,
  MODULE_PREFIX_BREAKING_EVENTS,
  MODULE_PREFIX_MAP,
} from "@/store";
import {
  GET_PREFERENCES,
  GET_DETAILS_SIDEBAR,
  GET_ANALYSIS_DETAILS,
  GET_TRANSLATED,
  GET_BREAKING_EVENTS_DETAILS,
  GET_MAP_PING,
} from "@/store/actions.type";
import { DETAIL_SIDEBAR_TYPES } from "@/common/constants/home";
import { MAP_ICON_COLORS } from "@/common/constants/maps";
import { translate } from "@/services/common/translator.service";
import { getArticles as getWODArticles } from "@/services/search/wod.service";
import { getFormatted, getFormattedDate } from "@/common/date.utilities";
import { parseStringToObject, formatBigNumber } from "@/common/basic.utilities";
import { getDistanceBetweenPoints } from "@/common/maps/utilities";
import { dataToCopy } from "@/services/search/wod.service";
import {
  getInfluencesArray,
  getPrimaryTopics,
  determinePrimaryTopics,
  getTopicsStringFromTopicsScores,
  formatCategoriesForV2,
} from "@/services/events/topics.service";
import {
  isAdmin,
  hasArticleHideImageAccess,
} from "@/services/user/user.service";
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  convertContentTypeToEventCategory,
} from "@/common/constants/analytics";
import { TRAVEL_RISK_RATING_ID } from "@/common/constants/controlRisks/risks";
import { openContentPage } from "@/common/eos.utilities";

const EMITTED_ACTIONS = {
  READ_CLICK: "read-click",
  UNREAD_CLICK: "unread-click",
  DELETE_CLICK: "delete-click",
  UPDATE_DOC_NOTE: "update-doc-note",
  LABEL_COLOR_CLICK: "label-color-click",
};

export default {
  name: "DetailsListItemCluster",
  props: {
    item: {
      type: Object,
      required: true,
    },
    theme: {
      type: String,
      required: false,
    },
    analyticsCategory: {
      type: String,
      required: false,
    },
    hideImage: {
      type: Boolean,
      default: false,
    },
    actionIconsEnabled: {
      type: Boolean,
      default: false,
    },
    acceptablePrimaryTopics: {
      type: Array,
      default: function () {
        let topics = [];
        const PRIMARY_TOPICS = getPrimaryTopics();
        for (let i = 0; i < PRIMARY_TOPICS.length; i++) {
          topics.push(PRIMARY_TOPICS[i].type);
        }
        const INFLUENCES_ARRAY = getInfluencesArray();
        for (let i = 0; i < INFLUENCES_ARRAY.length; i++) {
          topics.push(INFLUENCES_ARRAY[i].type);
        }
        return topics;
      },
      required: false,
    },
    showCollaboration: {
      type: Boolean,
      default: true,
    },
    showCopyIcon: {
      type: Boolean,
      default: true,
    },
    showingDocuments: {
      type: Boolean,
      default: false,
    },
    showFolderNotes: {
      type: Boolean,
      default: false,
    },
    showPrimaryTopics: {
      type: Boolean,
      default: true,
    },
    showThumbsDown: {
      type: Boolean,
      default: true,
    },
    showThumbsDownGeo: {
      type: Boolean,
      default: true,
    },
    getThresholdTypeFunction: {
      type: Function,
    },
    showInlineBody: {
      type: Boolean,
      default: false,
    },
    highlightEventOnMap: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Loader: () => import("@/components/common/Loader"),
    LeaveAppPopup: () => import("@/components/common/LeaveAppPopup"),
    DetailsActionIcons: () =>
      import("@/components/common/details/DetailsActionIcons"),
    DetailsListItemExtras: () =>
      import("@/components/common/details/DetailsListItemExtras"),
    DetailsListItemClickable: () =>
      import("@/components/common/details/DetailsListItemClickable"),
    DetailsListItemLocations: () =>
      import("@/components/common/details/DetailsListItemLocations.vue"),
    TrendIndicator: () => import("@/components/common/details/TrendIndicator"),
  },
  data() {
    let note = null;
    if (lodashGet(this.item, "properties.formatted.comments")) {
      note = this.item.properties.formatted.comments;
    }
    return {
      imgFailed: true,
      isRead: false,
      translated: false,
      translating: false,
      translatedArticles: [],
      showArticles: false,
      articles: null,
      articlesLoading: false,
      title: null,
      summary: null,
      showSummary: false,
      showBody: false,
      sourceLink: null,
      showSourceLink: null,
      documentNote: note,
      originalNote: note,
      showNote: this.showFolderNotes,
      noteJustAdded: false,
      noteSaving: false,
      sourcePopupText: null,
    };
  },
  computed: {
    ...mapGetters({
      preferences: MODULE_PREFIX_USER + "preferences",
      detailsSidebar: MODULE_PREFIX_HOME + "detailsSidebar",
      translatedStore: MODULE_PREFIX_SEARCH + "translated",
      maps: MODULE_PREFIX_MAP + "maps",
    }),
    esId() {
      if (lodashGet(this.item, "properties.formatted.esId")) {
        return this.item.properties.formatted.esId;
      }
      return null;
    },
    documentId() {
      if (lodashGet(this.item, "properties.formatted.documentId")) {
        return this.item.properties.formatted.documentId;
      }
      return null;
    },
    clusterId() {
      if (lodashGet(this.item, "properties.formatted.clusterId")) {
        return this.item.properties.formatted.clusterId;
      }
      return null;
    },
    clusterSize() {
      if (lodashGet(this.item, "properties.formatted.clusterSize")) {
        return this.item.properties.formatted.clusterSize;
      }
      return 0;
    },
    dateTime() {
      // if we have verified events, our date comes from another field
      if (this.verifiedEvent || this.isAnalysis) {
        if (lodashGet(this.item, "properties.formatted.published")) {
          return getFormatted(this.item.properties.formatted.published);
        }
      } else if (this.discoverAi) {
        // for discover ai, date comes from newest_date field
        if (lodashGet(this.item, "properties.formatted.newestDate")) {
          return getFormatted(this.item.properties.formatted.newestDate);
        }
      } else {
        if (lodashGet(this.item, "properties.formatted.occurred")) {
          return getFormatted(this.item.properties.formatted.occurred);
        }
      }
      return null;
    },
    eventTime() {
      if (lodashGet(this.item, "properties.formatted.occurred")) {
        return getFormattedDate(this.item.properties.formatted.occurred);
      }
      return null;
    },
    isTwitter() {
      return lodashGet(this.item.properties, "formatted.type") === "twitter";
    },
    isTwitterVerified() {
      return (
        this.isTwitter &&
        lodashGet(this.item.properties, "formatted.sourceVeracity") ===
          "Verified"
      );
    },
    srcLanguage() {
      if (lodashGet(this.item, "properties.formatted.language")) {
        return this.item.properties.formatted.language;
      }
      return null;
    },
    source() {
      if (lodashGet(this.item, "properties.formatted.source")) {
        return this.item.properties.formatted.source;
      }
      return null;
    },
    sentiment() {
      if (lodashHas(this.item, "properties.scores.tone")) {
        return this.item.properties.scores.tone;
      }
      if (lodashHas(this.item, "properties.formatted.sentiment")) {
        return this.item.properties.formatted.sentiment;
      }
      if (typeof lodashGet(this.item, "document.scores.tone") === "number") {
        return this.item.document.scores.tone;
      }
      return null;
    },
    emotions() {
      return this.item?.properties?.emotions || [];
    },
    reliability() {
      return (
        this.item?.properties?.formatted?.source_metadata?.reliability || null
      );
    },
    socialAuthenticity() {
      return (
        this.item?.properties?.formatted?.source_metadata
          ?.social_authenticity || null
      );
    },
    highRiskTag() {
      return this.item?.properties?.source_metadata?.high_risk ?? null;
    },
    sourceType() {
      return this.item?.properties?.source_metadata?.type ?? null;
    },
    socialFollowerCount() {
      let follower_count = this.item?.properties?.formatted?.source_metadata
        ?.follower_count;
      return !isNaN(follower_count) ? formatBigNumber(follower_count) : null;
    },
    author() {
      return this.item?.properties?.formatted?.author ?? null;
    },
    link() {
      if (lodashGet(this.item, "properties.formatted.link")) {
        return this.item.properties.formatted.link;
      }
      return null;
    },
    showImage() {
      return !(hasArticleHideImageAccess() || this.hideImage);
    },
    img() {
      if (lodashGet(this.item, "properties.formatted.img")) {
        this.setImgFailed(false);
        return this.item.properties.formatted.img;
      }
      return null;
    },
    userLanguage() {
      const LANG = lodashGet(this.preferences, "language.autoTranslateDest");
      return LANG && LANG.value ? LANG.value : "en";
    },
    languageMatches() {
      return !!(
        this.srcLanguage &&
        this.userLanguage &&
        this.userLanguage.toLowerCase() === this.srcLanguage.toLowerCase()
      );
    },
    showTranslateIcon() {
      if (
        this.verifiedEvent ||
        lodashGet(this.item, "properties.formatted.type") ===
          "risk-rating-change" ||
        lodashGet(this.item, "properties.source") === "evacuation"
      ) {
        return false;
      }

      if (this.userLanguage) {
        if (
          this.srcLanguage &&
          this.srcLanguage.toLowerCase() === "en" &&
          this.userLanguage.toLowerCase() === "en"
        ) {
          return false;
        }
        if (
          lodashGet(this.item, "properties.formatted.translated") &&
          this.userLanguage.toLowerCase() === "en"
        ) {
          return false;
        }
      }
      return true;
    },
    adminInfo() {
      if (isAdmin() && lodashGet(this.item, "properties.formatted.adminInfo")) {
        return this.item.properties.formatted.adminInfo;
      }
      return null;
    },
    hasDocumentNote() {
      return !!this.item?.properties?.formatted?.comments || this.noteJustAdded;
    },
    titleColor() {
      if (this.theme && this.theme === "light") {
        return {
          color: "black",
        };
      }
      return {
        color: "white",
      };
    },
    isDarkTheme() {
      return !(this.theme === "light");
    },
    cardStyle() {
      return this.isDarkTheme ? null : { padding: "0 8px" };
    },
    cardColor() {
      return this.isDarkTheme ? "transparent" : "white";
    },
    cardElevation() {
      return this.isDarkTheme ? "0" : "2";
    },
    readHint() {
      if (this.isRead) {
        return "Mark as unread";
      }
      return "Mark as read";
    },
    primaryTopic() {
      let topicScores = lodashGet(
        this.item,
        "properties.formatted.topic_scores"
      );
      if (!topicScores) {
        topicScores = lodashGet(this.item, "properties.topic_scores");
      }
      let isTwitter =
        lodashGet(this.item.properties, "formatted.type") === "twitter";
      return determinePrimaryTopics(
        topicScores,
        isTwitter,
        this.acceptablePrimaryTopics
      );
    },
    topicsDisplayList() {
      let topicScores = lodashGet(
        this.item,
        "properties.formatted.topic_scores"
      );
      if (topicScores) {
        let thresholdToUse = this.getThresholdTypeFunction(this.item);
        return getTopicsStringFromTopicsScores(topicScores, thresholdToUse);
      }
      return null;
    },
    influenceDisplayList() {
      let topicScores = lodashGet(
        this.item,
        "properties.formatted.topic_scores"
      );
      if (topicScores) {
        let thresholdToUse = this.getThresholdTypeFunction(this.item);
        return getTopicsStringFromTopicsScores(
          topicScores,
          thresholdToUse,
          true
        );
      }
      return null;
    },
    alertName() {
      if (lodashGet(this.item, "properties.formatted.name")) {
        return this.item.properties.formatted.name;
      }
      return null;
    },
    verifiedEvent() {
      if (lodashGet(this.item, "properties.formatted.type")) {
        return (
          this.item.properties.formatted.type === "maritime" ||
          this.item.properties.formatted.type === "political"
        );
      }
      return null;
    },
    discoverAi() {
      return this.item.properties.formatted.type === "headlines";
    },
    headlineStatus() {
      if (lodashGet(this.item, "properties.formatted.tagName")) {
        return this.item.properties.formatted.tagName;
      }
      return null;
    },
    headlineStatusDisplayName() {
      return this.headlineStatus === "accelerating"
        ? "Accelerating story"
        : "New";
    },
    statusStyle() {
      return {
        backgroundColor:
          this.headlineStatusDisplayName === "New" ? "#43bd6e" : "#fdfcca",
      };
    },
    headlineStatusText() {
      if (this.headlineStatusDisplayName === "Accelerating story") {
        return "The number of new articles in the past hour exceeds the 12-hour average";
      } else {
        return "This story is less than six hours old";
      }
    },
    eventsAiArticles() {
      if (lodashGet(this.item, "properties.formatted.eventsAI")) {
        return this.item.properties.formatted.eventsAI;
      }
      return null;
    },
    headlineCountry() {
      return lodashGet(this.item, "properties.formatted.country");
    },
    relatedCountries() {
      const countries = lodashGet(
        this.item,
        "properties.formatted.secondaryCountries"
      );
      if (countries && Array.isArray(countries) && countries.length > 0) {
        return countries;
      }
      return null;
    },
    categories() {
      const categoryName = lodashGet(this.item, "properties.categoryName");
      const categories = lodashGet(this.item, "properties.labeled_categories");
      return categoryName
        ? categoryName
        : categories
        ? formatCategoriesForV2(categories)
        : null;
    },
    assets() {
      let assets = lodashGet(this.item, "properties.assets");
      if (assets) {
        let _assets = [];
        assets.forEach((asset) => {
          if (asset.name) {
            _assets.push(asset.name);
          }
        });
        return _assets.join(", ");
      }
      return null;
    },
    getLatLon() {
      //Items retrieved from gdelt will have geometry.coordinates
      if (this.item.geometry?.coordinates) {
        let coordinates = lodashGet(this.item, "geometry.coordinates");
        if (
          coordinates &&
          coordinates[0] &&
          coordinates[1] &&
          !isNaN(coordinates[0]) &&
          !isNaN(coordinates[1])
        ) {
          return {
            lat: coordinates[1].toFixed(5),
            lon: coordinates[0].toFixed(5),
          };
        }
      }
      //Items retrieved from collaboration will only have a location
      // TODO: Support multiple locations
      else if (this.item.document?.location) {
        let location = Array.isArray(this.item.document.location)
          ? this.item.document.location[0]
          : this.item.document.location;

        if (
          typeof location.lat === "number" &&
          typeof location.lon === "number"
        ) {
          return location;
        }
      }

      return null;
    },
    severity() {
      return lodashGet(this.item, "properties.formatted.severity");
    },
    damageRating() {
      return lodashGet(this.item, "properties.formatted.damageRatingName");
    },
    sectors() {
      let sectors = lodashGet(this.item, "properties.sectors");
      if (sectors) {
        let _sectors = [];
        sectors.forEach((sector) => {
          if (sector.name) {
            _sectors.push(sector.name);
          }
        });
        return _sectors.join(", ");
      }
      return null;
    },
    analysisInfo() {
      if (this.isAnalysis) {
        return {
          risks: lodashGet(this.item, "properties.formatted.risks"),
          countries: lodashGet(
            this.item,
            "properties.formatted.countriesWithFlag"
          ),
          published: lodashGet(this.item, "properties.formatted.published"),
          updated: lodashGet(this.item, "properties.formatted.updated"),
          tags: lodashGet(this.item, "properties.formatted.tags"),
          travelSource: lodashGet(
            this.item,
            "properties.formatted.travelSource"
          ),
          travelLevel: lodashGet(this.item, "properties.formatted.travelLevel"),
          travelCategories: lodashGet(
            this.item,
            "properties.formatted.travelCategories"
          ),
        };
      } else {
        return {};
      }
    },
    countriesWithFlags() {
      return lodashGet(this.item, "properties.formatted.countriesWithFlag");
    },
    countriesDisplayMetadata() {
      return lodashGet(
        this.item,
        "properties.formatted.countriesDisplayMetadata"
      );
    },
    isAnalysis() {
      return lodashGet(this.item, "properties.formatted.type") === "analysis";
    },
    isBreakingEvent() {
      return (
        lodashGet(this.item, "properties.formatted.type") === "breaking-event"
      );
    },
    waters() {
      return this.item?.properties?.formatted?.waters;
    },
    maritimeSources() {
      let maritimeSources = lodashGet(
        this.item,
        "properties.formatted.maritimeSources"
      );
      if (maritimeSources) {
        let _maritimeSources = [];
        maritimeSources.forEach((source) => {
          _maritimeSources.push(source);
        });
        return _maritimeSources.join(", ");
      }
      return null;
    },
    trend() {
      return lodashGet(this.item, "properties.trend");
    },
    references() {
      if (lodashGet(this.item, "properties.formatted.references")) {
        return this.item.properties.formatted.references;
      }
      return null;
    },
    originalLanguage() {
      return lodashGet(this.item, "properties.formatted.originalLanguage");
    },
    isTravelISOS() {
      return (
        (this.item.properties.risks &&
          this.item.properties.risks
            .map((c) => c.id)
            .includes(TRAVEL_RISK_RATING_ID)) ||
        this.item.properties.formatted?.risks?.includes("Travel")
      );
    },
    assetProximityText() {
      if (
        this.getLatLon &&
        this.detailsSidebar.selected === DETAIL_SIDEBAR_TYPES.assets &&
        lodashGet(this.detailsSidebar, "features[0].properties.latitude") &&
        lodashGet(this.detailsSidebar, "features[0].properties.longitude") &&
        lodashGet(this.detailsSidebar, "features[0].properties.title")
      ) {
        const assetLatLng = {
          lat: this.detailsSidebar.features[0].properties.latitude,
          lon: this.detailsSidebar.features[0].properties.longitude,
        };
        const assetTitle = this.detailsSidebar.features[0].properties.title;
        const units = this.preferences?.poisDistUnits === "miles" ? "mi" : "km";
        const distance = getDistanceBetweenPoints(
          this.getLatLon,
          assetLatLng,
          units
        );
        if (distance) {
          return `This event occurred ${distance.toFixed(
            1
          )} ${units} from ${assetTitle}`;
        }
      }
      return null;
    },
    highlightSelectedItemType() {
      return !this.isAnalysis;
    },
    initialPublishedDate() {
      if (lodashGet(this.item, "properties.formatted.initialPublishedDate")) {
        return getFormatted(
          this.item.properties.formatted.initialPublishedDate
        );
      }
      return this.dateTime;
    },
    revisedPublishedDate() {
      if (lodashGet(this.item, "properties.formatted.revisedPublishedDate")) {
        return getFormatted(
          this.item.properties.formatted.revisedPublishedDate
        );
      }
      return null;
    },
    displayRevisedDate() {
      return {
        label: this.revisedPublishedDate ? "Revised Date" : "Published Date",
        date: this.revisedPublishedDate
          ? this.revisedPublishedDate
          : this.initialPublishedDate,
      };
    },
    isFollowerCountBracketUnlocked() {
      return this.featureUnlocked(
        "livesearch-source-metadata-social-follower-count"
      );
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.getPrefs();
      this.item.properties = parseStringToObject(
        this.item.properties,
        "formatted"
      );

      let isTwitter =
        lodashGet(this.item.properties, "formatted.type") === "twitter";
      let isWeibo =
        lodashGet(this.item.properties, "formatted.type") === "weibo";
      this.showSummary = isTwitter || isWeibo;
      this.showMoreLess = isWeibo;

      if (lodashGet(this.item.properties, "formatted.type") === "twitter") {
        if (this.item.properties.formatted.summary) {
          // remove URL within summary
          let summary = this.item.properties.formatted.summary.replace(
            /(?:https?|ftp):\/\/[\n\S]+/g,
            ""
          );
          if (!isTwitter) {
            title += ": " + summary;
          }
        }
      }
      this.setIsRead(this.item.properties.formatted.read);
      this.addArticleToTranslatedList(this.item.properties.formatted);
      this.articles = null;
    },
    addArticleToTranslatedList(data) {
      this.$set(this.translatedArticles, this.translatedArticles.length, {
        esId: data.esId,
        title: {
          original: data.title,
          translated: null,
        },
        summary: {
          original:
            typeof data.summary === "string" && data.summary.length
              ? data.summary.substring(0, 1000)
              : null,
          translated: null,
        },
        body: {
          original:
            typeof data.body === "string" && data.body.length
              ? data.body
              : null,
          translated: null,
        },
        sanitizedBody: {
          original:
            typeof data.sanitizedBody === "string" && data.sanitizedBody.length
              ? data.sanitizedBody
              : null,
          translated: null,
        },
        translated: false,
        translating: false,
      });
    },
    toggleSummary() {
      if (this.discoverAi) {
        const id = this.item.properties._source.headline_id;
        openContentPage(`/discover/headlines/${id}`, {
          event: "open.headline",
          state: id,
        });
        return;
      }
      this.highlightEventLocationOnMap();
      if (this.showInlineBody) {
        this.showBody = !this.showBody;
        if (this.analyticsCategory && this.showBody) {
          let message = `Show Body --- ${this.item.properties.id}`;

          if (this.isTravelISOS) {
            message = `Show Body --- ISOS: Travel Alert --- ${this.item.properties.id}`;
          }

          this.handlePageTrackerEvent(
            this.analyticsCategory,
            EVENT_ACTIONS.expanded,
            message
          );
        } else if (this.analyticsCategory && !this.showBody) {
          this.handlePageTrackerEvent(
            this.analyticsCategory,
            EVENT_ACTIONS.closed,
            "Close Body"
          );
        }
      } else {
        this.showSummary = !this.showSummary;
        if (this.analyticsCategory && this.showSummary) {
          let message = `Show Summary --- ${this.item.properties.id}`;

          if (this.isTravelISOS) {
            message = `Show Summary --- ISOS: Travel Alert --- ${this.item.properties.id}`;
          }

          this.handlePageTrackerEvent(
            this.analyticsCategory,
            EVENT_ACTIONS.expanded,
            message
          );
        } else if (this.analyticsCategory && !this.showSummary) {
          this.handlePageTrackerEvent(
            this.analyticsCategory,
            EVENT_ACTIONS.closed,
            "Close Summary"
          );
        }
      }
    },
    async toggleArticles() {
      this.showArticles = !this.showArticles;
      if (this.showArticles && !this.articles) {
        await this.getArticles();
      }
    },
    getPrefs() {
      this.$store.dispatch(MODULE_PREFIX_USER + GET_PREFERENCES);
    },
    async getArticles() {
      try {
        this.articlesLoading = true;
        let result;
        let wodParams = {};
        wodParams.clusterId = this.clusterId;
        wodParams.clusterArticle = true;
        wodParams.noStartEnd = true;

        result = await getWODArticles(wodParams);

        if (result && result.features && Array.isArray(result.features)) {
          this.articles = result.features;
          this.articles.forEach((article) => {
            this.addArticleToTranslatedList(article.properties.formatted);
          });
        } else {
          this.articles = null;
        }

        this.articlesLoading = false;
      } catch (error) {
        console.error(
          "Error in getArticles in DetailsListItemCluster: " + error
        );
        this.articles = null;
        this.articlesLoading = false;
      }
    },
    getArticleDate(inDate) {
      if (inDate) {
        return getFormatted(inDate);
      }
      return null;
    },
    getArticleTitle(esId) {
      return this.getArticleInfo(esId, "title");
    },
    getArticleSummary(esId) {
      return this.getArticleInfo(esId, "summary");
    },
    getArticleBody(esId) {
      return this.getArticleInfo(esId, "body");
    },
    getArticleTranslating(esId) {
      return this.getArticleInfo(esId, "translating");
    },
    getArticleTranslateHint(esId) {
      return this.getArticleInfo(esId, "hint");
    },
    getArticleInfo(esId, type) {
      if (esId) {
        const article = lodashFind(this.translatedArticles, ["esId", esId]);
        if (article) {
          if (type === "translating") {
            return article.translating;
          }
          if (type === "hint") {
            if (article.translated) {
              return "Undo Translation";
            }

            if (
              lodashGet(this.preferences, "language.autoTranslateDest.name")
            ) {
              return `Translate to ${lodashGet(
                this.preferences,
                "language.autoTranslateDest.name"
              )}`;
            } else {
              return "Translate to English";
            }
          }
          if (article.translated && lodashGet(article, `${type}.translated`)) {
            return article[type].translated;
          }
          if (lodashGet(article, `${type}.original`)) {
            return article[type].original;
          }
        }
      }
      return null;
    },
    async translate(data) {
      let article = lodashFind(this.translatedArticles, ["esId", data.esId]);

      if (article) {
        article.translating = true;
        if (!lodashGet(article, "title.translated")) {
          const text = [];
          if (data.title) {
            text.push(data.title);
          }
          if (data.summary) {
            text.push(data.summary);
          }
          let originalSummary = data.summary;
          if (data.type === "analysis" && data.sanitizedBody) {
            text.push(data.sanitizedBody);
            originalSummary = data.sanitizedBody;
          }
          if (data.type === "analysis" && data.body) {
            text.push(data.body);
          }
          let translatedText = await this.translateText(text);
          if (
            translatedText &&
            Array.isArray(translatedText) &&
            translatedText.length
          ) {
            if (translatedText[0]) {
              article.title.translated = translatedText[0];
            }
            if (translatedText[1]) {
              article.summary.translated = translatedText[1].substring(0, 1000);
            }
            if (data.type === "analysis" && translatedText[2]) {
              article.sanitizedBody = { original: data.sanitizedBody };
              article.sanitizedBody.translated = translatedText[2];
            }
            if (data.type === "analysis" && translatedText[3]) {
              article.body = { original: data.body };
              article.body.translated = translatedText[3];
            }
          }

          const translatedStore = lodashCloneDeep(this.translatedStore);
          translatedStore[data.esId] = {
            title: article.title.translated || data.title,
            summary: article.summary.translated || originalSummary,
          };
          this.$store.dispatch(
            MODULE_PREFIX_SEARCH + GET_TRANSLATED,
            translatedStore
          );
        }
        article.translated = !article.translated;
        article.translating = false;
      }
    },
    async translateText(textArr) {
      let params = {
        text: textArr,
        dest: "en",
      };
      if (lodashGet(this.preferences, "language.autoTranslateDest.value")) {
        params.dest = lodashGet(
          this.preferences,
          "language.autoTranslateDest.value"
        );
      }

      const RESULT = await translate(params);
      if (RESULT && Array.isArray(RESULT) && RESULT.length) {
        let results = [];
        RESULT.forEach((item) => {
          if (item && item.translatedText) {
            results.push(item.translatedText);
          }
        });
        return results;
      }
      return null;
    },
    onImgError() {
      this.setImgFailed(true);
    },
    setImgFailed(value) {
      this.imgFailed = value;
    },
    setIsRead(value) {
      this.isRead = value;
    },
    handleReadClick(documentId) {
      this.setIsRead(!this.isRead);
      if (this.isRead) {
        this.$emit(EMITTED_ACTIONS.READ_CLICK, documentId);
      } else {
        this.$emit(EMITTED_ACTIONS.UNREAD_CLICK, documentId);
      }
    },
    handleDeleteClick(documentId) {
      this.$emit(EMITTED_ACTIONS.DELETE_CLICK, documentId);
    },
    openSource(url) {
      this.sourceLink = url;
      this.showSourceLink = new Date().toISOString();
    },
    handleUpdateDocumentNote() {
      this.noteJustAdded = true;
      this.noteSaving = true;
      lodashDebounce(async () => {
        this.$emit(EMITTED_ACTIONS.UPDATE_DOC_NOTE, {
          documentId: this.documentId,
          comments: this.documentNote,
          revert: () => {
            this.documentNote = this.originalNote;
            this.noteSaving = false;
          },
          finished: () => {
            this.noteSaving = false;
          },
        });
      }, 1000)();
    },
    async handleClickShowAnalysisDetails() {
      this.detailsSidebar.visible = true;
      this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.analysis;
      this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.analysis;

      const PARAMS = {
        title: "Analysis",
        feature: this.item,
      };
      this.$store.dispatch(
        MODULE_PREFIX_ANALYSIS + GET_ANALYSIS_DETAILS,
        PARAMS
      );

      await this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
        this.detailsSidebar
      );

      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.analysis,
        EVENT_ACTIONS.clicked,
        `Analysis click --- ${this.item.properties.id}`
      );
    },
    handleLabelColorClick(obj) {
      this.$emit(EMITTED_ACTIONS.LABEL_COLOR_CLICK, obj);
    },
    handleDocumentNoteClick() {
      this.showNote = !this.showNote;
    },
    async handleCopyClick(item) {
      document.activeElement.blur();

      const colsToCopy = [
        "title",
        "risks",
        "published",
        "occurred",
        "severity",
        "maritimeSources",
        "body",
        "countries",
        "waters",
      ];

      if (!this.verifiedEvent && !this.isBreakingEvent) {
        colsToCopy.push("summary");
        item.summary = this.getArticleSummary(item.esId);
      }

      if (this.isBreakingEvent) {
        if (item.countriesDisplayMetadata) {
          item.countries = item.countriesDisplayMetadata;
        }
      }

      item.title = this.getArticleTitle(item.esId);
      item.body = this.getArticleBody(item.esId);

      // if content doesnt have body, remove the property from the copy columns
      if (!item.body) {
        const index = colsToCopy.indexOf("body");
        if (index !== -1) {
          colsToCopy.splice(index, 1);
        }
      }

      if (this.topicsDisplayList) {
        item.events = this.topicsDisplayList;
        colsToCopy.push("events");
      } else if (this.categories) {
        item.categories = this.categories;
        colsToCopy.push("categories");
      }

      if (this.sectors) {
        item.sectors = this.sectors;
        colsToCopy.push("sectors");
      }

      if (this.assets) {
        item.assets = this.assets;
        colsToCopy.push("assets");
      }

      let latLon = this.getLatLon;
      if (latLon) {
        item.location = `${latLon.lat}, ${latLon.lon}`;
        colsToCopy.push("location");
      }

      if (item.link) {
        colsToCopy.push("link");
      }

      this.handlePageTrackerEvent(
        convertContentTypeToEventCategory(item.type),
        EVENT_ACTIONS.clicked,
        `Copy To Clipboard --- ${item.esId}`
      );
      await dataToCopy(item, colsToCopy, true);
    },
    async handleClickShowBreakingEventDetails() {
      this.detailsSidebar.visible = true;
      this.detailsSidebar.selected = DETAIL_SIDEBAR_TYPES.breakingEvents;
      this.detailsSidebar.type = DETAIL_SIDEBAR_TYPES.breakingEvents;

      const PARAMS = {
        title: "Breaking Event",
        feature: this.item,
      };
      this.$store.dispatch(
        MODULE_PREFIX_BREAKING_EVENTS + GET_BREAKING_EVENTS_DETAILS,
        PARAMS
      );

      await this.$store.dispatch(
        MODULE_PREFIX_HOME + GET_DETAILS_SIDEBAR,
        this.detailsSidebar
      );

      this.handlePageTrackerEvent(
        EVENT_CATEGORIES.breaking_events,
        EVENT_ACTIONS.clicked,
        `Breaking Event click --- ${this.item.properties.id}`
      );
    },
    highlightEventLocationOnMap() {
      if (
        this.highlightEventOnMap &&
        this.highlightSelectedItemType &&
        !this.showSummary &&
        this.getLatLon?.lat &&
        this.getLatLon?.lon
      ) {
        const pingParams = {
          feature: {
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [
                parseFloat(this.getLatLon.lon),
                parseFloat(this.getLatLon.lat),
              ],
            },
          },
          color: MAP_ICON_COLORS.alerts,
        };
        this.$store.dispatch(MODULE_PREFIX_MAP + GET_MAP_PING, pingParams);
        lodashDebounce(async () => {
          this.$store.dispatch(MODULE_PREFIX_MAP + GET_MAP_PING, null);
        }, 5000)();
      }
    },
  },
  watch: {
    item: {
      handler(newValue, oldValue) {
        if (!lodashIsEqual(newValue, oldValue)) {
          this.init();
        }
      },
      deep: true,
    },
    showFolderNotes: {
      handler() {
        this.showNote = this.showFolderNotes;
      },
    },
  },
  beforeDestroy() {
    this.articles = null;
    this.articlesLoading = false;
    this.sourceLink = null;
    this.showSourceLink = null;
    this.isRead = null;
    this.translatedArticles = null;
  },
};
</script>

<style scoped lang="scss">
/* Used https://codepen.io/sosuke/pen/Pjoqqp with a hex color of #1DA1F2 based on https://usbrandcolors.com/twitter-colors/ */
.filter-blue {
  filter: invert(64%) sepia(43%) saturate(5722%) hue-rotate(175deg)
    brightness(96%) contrast(97%);
}
.verifiedImg {
  display: inline-block;
  top: 0.25em;
}
.hyp-date-source-container {
  font-size: 10px;
}
.hyp-title-container {
  max-height: 48px;
  overflow: hidden;
}
.hyp-sources-container {
  max-width: 320px;
  display: block;
}
.hyp-title-container.clickable {
  cursor: pointer;
}
.hyp-title {
  width: 100%;
}
.hyp-toggle-container {
  font-size: 11px;
  cursor: pointer;
}
.hyp-more-info {
  color: $primary-color !important;
}
.hyp-more-info-toggle {
  position: relative;
  top: -2px;
}
.hyp-img {
  width: 78px;
}
.hyp-divider {
  border-color: #3c3c3c !important;
}
.hyp-translating-container {
  position: relative;
  top: 5px;
}
.hyp-translating-icon {
  cursor: pointer;
}
.hyp-admin-info {
  cursor: pointer;
}
.hyp-item-read-icon {
  cursor: pointer;
  margin-top: 2px;
}
.hyp-article-is-read {
  opacity: 0.5;
}
.v-card .theme--dark {
  -webkit-box-shadow: none !important;
  box-shadow: none !important;
}
.hyp-divider-bar {
  padding-left: 5px;
  padding-right: 5px;
  font-size: 11px;
}
.hyp-translate-section {
  margin-right: 5px;
}
.hyp-doc-note-textarea {
  font-size: 12px;
}
.hyp-source-alert-title {
  font-size: 11px;
}
.flexVerticalAlign {
  display: flex;
  align-items: center;
}
.breakText {
  word-wrap: break-word;
  word-break: break-word;
}
.hyp-details-list-item-topics {
  font-size: 10px;
  line-height: 1.6em;
}
.ai-articles {
  padding: 4px 8px;
}

.trendIndicator {
  float: left;
  position: relative;
  margin-right: 5px;
}
</style>

<style>
.item-body-dark-mode * {
  background-color: #191a1a !important;
  color: white !important;
}
</style>
